import React, { useContext, useState } from 'react';
import { Timeline, TimelineItem, TimelineSeparator, TimelineConnector, TimelineContent, TimelineDot } from '@mui/lab';
import { Paper, Box, Grid, Typography, Chip, Stack, Modal, IconButton, Fade, Link, Tooltip, Zoom } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import { useLoaderData } from 'react-router-dom';
import _ from 'lodash';
import { useTheme } from '../utils/ThemeContext';
import WorkHistory from '@mui/icons-material/WorkHistory';
import { ReCaptchaContext } from './recatcha-provider';
import { experienceStyles } from '../styles/experience.styles';

// Basic Company Card Component
const CompanyCard = ({ company, onClick }) => (
    <Paper 
        elevation={3} 
        onClick={onClick}
        sx={experienceStyles.companyCard}
    >
        <Stack spacing={2}>
            <Typography 
                variant="h5" 
                component="h3" 
                color="primary"
                sx={experienceStyles.companyName}
            >
                {!_.isNil(company.acquiredBy) ? (
                    <>
                        {company.name}
                        <Tooltip 
                            title={`View acquisition details of ${company.name} by ${company.acquiredBy}`}
                            slots={{ transition: Zoom }}
                            arrow
                            placement="top"
                        >
                            <Link 
                                href={company.acquisitionLink} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={experienceStyles.acquisitionLink}
                            >
                                <MergeTypeIcon className="merge-icon" sx={{ mr: 0.5 }} />
                                Acquired by {company.acquiredBy}
                                <OpenInNewIcon className="external-icon" sx={{ ml: 0.5 }} />
                            </Link>
                        </Tooltip>
                    </>
                ) : company.name}
            </Typography>
            <Typography 
                variant="body2" 
                color="text.secondary"
                sx={experienceStyles.duration}
            >
                <BusinessIcon fontSize="small" />
                {company.duration}
            </Typography>
            <Typography 
                variant="h6"
                color="text.primary"
                sx={experienceStyles.currentRole}
            >
                {company.currentRole}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {company.technologies?.slice(0, 4).map((tech) => (
                    <Chip 
                        key={tech} 
                        label={tech} 
                        size="small" 
                        sx={experienceStyles.technologyChip}
                    />
                ))}
                {company.technologies?.length > 4 && (
                    <Chip 
                        label={`+${company.technologies.length - 4} more`}
                        size="small"
                        sx={experienceStyles.moreChip}
                    />
                )}
            </Stack>
        </Stack>
    </Paper>
);

// Detailed Timeline View Component
const CompanyTimeline = ({ company, isDarkMode }) => (
    <Timeline position="alternate">
        {company.roles.map((role, index) => (
            <TimelineItem key={index}>
                <TimelineSeparator>
                    <TimelineDot 
                        sx={{
                            background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                            boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                        }}
                    >
                        <BusinessIcon />
                    </TimelineDot>
                    {index < company.roles.length - 1 && (
                        <TimelineConnector sx={{ 
                            background: (theme) => `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                            width: '2px'
                        }} />
                    )}
                </TimelineSeparator>
                <TimelineContent>
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: 3,
                            background: 'rgba(255, 255, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: '0 8px 16px rgba(0,0,0,0.12)'
                            }
                        }}
                    >
                        <Stack spacing={2}>
                            <Typography 
                                variant="h6" 
                                component="h3" 
                                color="primary"
                                sx={{ 
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                {role.position}
                            </Typography>
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                    fontStyle: 'italic',
                                    opacity: 0.8
                                }}
                            >
                                {role.duration}
                            </Typography>
                            {!_.isEmpty(role.responsibilities) && (
                                <Stack spacing={1.5}>
                                    {role.responsibilities.map((responsibility, idx) => (
                                        <Typography 
                                            key={idx} 
                                            variant="body2" 
                                            sx={{ 
                                                display: 'flex', 
                                                alignItems: 'flex-start',
                                                gap: 1,
                                                color: 'text.primary',
                                                '&::before': {
                                                    content: '"•"',
                                                    color: (theme) => theme.palette.primary.main,
                                                    fontWeight: 'bold'
                                                }
                                            }}
                                        >
                                            {responsibility}
                                        </Typography>
                                    ))}
                                </Stack>
                            )}
                            <Stack 
                                direction="row" 
                                spacing={1} 
                                flexWrap="wrap" 
                                gap={1}
                                sx={{ mt: 2 }}
                            >
                                {role.technologies.map((tech) => (
                                    <Chip 
                                        key={tech} 
                                        label={tech} 
                                        size="small" 
                                        sx={{ 
                                            background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                                            color: 'text.primary',
                                            '&:hover': {
                                                background: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'
                                            }
                                        }} 
                                    />
                                ))}
                            </Stack>
                        </Stack>
                    </Paper>
                </TimelineContent>
            </TimelineItem>
        ))}
    </Timeline>
);

const Experience = () => {
    const data = useLoaderData();
    const { experiences } = data;
    const [selectedCompany, setSelectedCompany] = useState(null);
    const { isDarkMode } = useTheme();
    const { captchaVerified, onCaptchaVerify } = useContext(ReCaptchaContext);

    const handleCompanyClick = async (company) => {
      if (captchaVerified) {
        setSelectedCompany(company);
        return;
      }

      if (process.env.NODE_ENV === 'production') {
        const status = await onCaptchaVerify();
        if (status) {
          setSelectedCompany(company);
        }
      } else {
        setSelectedCompany(company);
      }
        
    };

    const handleCloseModal = () => {
        setSelectedCompany(null);
    };

    // Separate current and previous experiences using type property
    const currentExperience = experiences.find(exp => exp.type === 'current');
    const previousExperiences = experiences.filter(exp => exp.type !== 'current');

    return (
        <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 3 }}>
            <Fade in timeout={800}>
                <Stack spacing={4}>
                    {/* Current Role */}
                    {currentExperience && (
                        <Box>
                            <Typography 
                                variant="h6" 
                                component="h3" 
                                sx={{ 
                                    mb: 2,
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <BusinessIcon fontSize="small" />
                                Current Role
                            </Typography>
                            <Grid container>
                                <Grid item xs={12}>
                                    <Paper 
                                        elevation={3} 
                                        onClick={() => handleCompanyClick(currentExperience)}
                                        sx={{ 
                                            p: 3, 
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease-in-out',
                                            '&:hover': { 
                                                transform: 'scale(1.01)',
                                                boxShadow: '0 10px 20px rgba(0,0,0,0.12)',
                                            },
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '4px',
                                                background: (theme) => `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`
                                            }
                                        }}
                                    >
                                        <Stack spacing={2}>
                                            <Typography 
                                                variant="h5" 
                                                component="h3" 
                                                color="primary"
                                                sx={{ 
                                                    fontWeight: 600,
                                                    mb: 1
                                                }}
                                            >
                                                {currentExperience.name}
                                            </Typography>
                                            <Typography 
                                                variant="body2" 
                                                color="text.secondary"
                                                sx={{ 
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1
                                                }}
                                            >
                                                {currentExperience.duration}
                                            </Typography>
                                            <Typography 
                                                variant="h6"
                                                color="text.primary"
                                                sx={{ 
                                                    fontWeight: 500,
                                                    fontSize: '1.1rem'
                                                }}
                                            >
                                                {currentExperience.currentRole}
                                            </Typography>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                                                {currentExperience.technologies?.slice(0, 4).map((tech) => (
                                                    <Chip 
                                                        key={tech} 
                                                        label={tech} 
                                                        size="small" 
                                                        sx={{ 
                                                            background: 'rgba(255, 255, 255, 0.1)',
                                                            color: 'text.primary',
                                                            '&:hover': {
                                                                background: 'rgba(255, 255, 255, 0.15)'
                                                            }
                                                        }} 
                                                    />
                                                ))}
                                                {currentExperience.technologies?.length > 4 && (
                                                    <Chip 
                                                        label={`+${currentExperience.technologies.length - 4} more`}
                                                        size="small"
                                                        sx={{ 
                                                            background: 'rgba(255, 255, 255, 0.05)',
                                                            color: 'text.secondary'
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </Box>
                    )}

                    {/* Previous Roles */}
                    {previousExperiences.length > 0 && (
                        <Box>
                            <Typography 
                                variant="h6" 
                                component="h3" 
                                sx={{ 
                                    mb: 2,
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}
                            >
                                <WorkHistory fontSize="small" />
                                Previous Roles
                            </Typography>
                            <Grid container spacing={3}>
                                {previousExperiences.map((company, index) => (
                                    <Grid item xs={12} md={6} key={index}>
                                        <CompanyCard 
                                            company={company}
                                            onClick={() => handleCompanyClick(company)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    )}
                </Stack>
            </Fade>

            <Modal
                open={Boolean(selectedCompany)}
                onClose={handleCloseModal}
                closeAfterTransition
                sx={experienceStyles.modal}
            >
                <Fade in={Boolean(selectedCompany)}>
                    <Box sx={experienceStyles.modalContent}>
                        <IconButton
                            onClick={handleCloseModal}
                            sx={experienceStyles.closeButton}
                        >
                            <CloseIcon />
                        </IconButton>
                        {selectedCompany && (
                            <>
                                <Typography 
                                    variant="h4" 
                                    component="h2" 
                                    gutterBottom 
                                    sx={experienceStyles.modalTitle}
                                >
                                    {selectedCompany.name}
                                </Typography>
                                <CompanyTimeline company={selectedCompany} isDarkMode={isDarkMode} />
                            </>
                        )}
                    </Box>
                </Fade>
            </Modal>
        </Box>
    );
};

export default Experience;